import { useCallback } from 'react';

import {
  useAllZoomRecordingsQuery,
  AllZoomRecordingsQuery,
  AllZoomRecordingsQueryVariables,
} from 'core/graphql';

type ZoomRecordingFilters = Omit<
  AllZoomRecordingsQueryVariables,
  'first' | 'after'
>;

/**
 * The recordings-list node — the shared `ZoomRecordingFields` fragment PLUS the
 * inline `lessonRecordings` selection (video → lesson nav). Derived from the
 * query type so the nav link stays typed.
 */
export type ZoomRecordingRow = NonNullable<
  NonNullable<
    NonNullable<AllZoomRecordingsQuery['allZoomRecordings']>['edges'][number]
  >['node']
>;

interface IZoomRecordingsHook {
  data: ZoomRecordingRow[];
  loading: boolean;
  error?: string;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/** Relay-cursor paginated list of raw ZoomRecording rows (incl. orphans). */
export const useZoomRecordings = (
  filters: ZoomRecordingFilters = {},
  pageSize = 50,
): IZoomRecordingsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = useAllZoomRecordingsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.allZoomRecordings;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    // ТЗ 0043: только `after`. Ручной `updateQuery` убран — он склеивал
    // страницы поверх `lastResult.data`, то есть поверх ПРЕДЫДУЩЕГО
    // отрисованного списка, а не списка для текущих переменных, и при смене
    // группы дописывал её занятия в чужой фильтр. Теперь страницы сшивает
    // `relayStylePagination` в `core/cache/cache.ts`, где каждая комбинация
    // фильтров — свой список.
    fetchMore({
      variables: { after: connection.pageInfo.endCursor },
    });
  }, [connection, fetchMore]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is ZoomRecordingRow => Boolean(node)),
    loading,
    error: error?.message,
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
